<template>
  <div class="WSelect__wrapper">
    <ComboboxRoot
      class="ComboboxRoot"
      :modelValue="modelValue"
      v-model:searchTerm="searchTerm"
      :open="open"
      @update:open="handleOpenState"
      :multiple="multiple"
      :disabled="disabled"
      :filterFunction="filterFunction || filterOptions"
      :display-value="
        () => {
          const selected = options?.find((e: any) => e.value === modelValue);
          return selected?.label;
        }
      "
    >
      <!-- input and trigger section -->
      <ComboboxAnchor class="ComboboxAnchor" :class="{ optionDisabled: disabled }">
        <ComboboxTrigger
          class="ComboboxTrigger"
          :class="{
            multiple,
            hasFocus: open || focussed,
            hasValue: multiple ? hasValidMultipleValue : hasValidSingleValue,
            hasPlaceholder: placeholder,
            singleDisplay: singleDisplay,
            hidePlaceholderOnFocus: hidePlaceholderOnFocus,
            hasCustomTrigger: $slots.trigger, // NEW: class for custom trigger styling
          }"
        >
          <!-- NEW: Custom trigger slot -->
          <template v-if="$slots.trigger">
            <div class="flex-1 custom-trigger-content">
              <slot
                name="trigger"
                :modelValue="modelValue"
                :selectedOption="options?.find((e: any) => e.value === modelValue)"
                :selectedOptions="
                  multiple && Array.isArray(modelValue)
                    ? modelValue.map(val => options?.find((e: any) => e.value === val)).filter(Boolean)
                    : []
                "
                :placeholder="placeholder"
                :open="open"
                :focussed="focussed"
                :multiple="multiple"
                :disabled="disabled"
              />
            </div>
          </template>

          <!-- EXISTING: Default trigger content (fallback) -->
          <template v-else>
            <div class="flex-1 flex flex-col">
              <!-- floating placeholder -->
              <ComboboxLabel
                class="placeholder"
                :class="{
                  singleDisplayPlaceholder: singleDisplay,
                  hidden: hidePlaceholderOnFocus && (open || focussed || (multiple ? hasValidMultipleValue : hasValidSingleValue)),
                }"
                v-if="!(singleDisplay && (hasValidMultipleValue || hasValidSingleValue))"
              >
                <iconify-icon icon="fa6-solid:magnifying-glass" class="search-icon" v-if="showSearchIcon" />
                {{ placeholder }}
              </ComboboxLabel>

              <!-- multi-select values -->
              <div class="chips" v-if="Array.isArray(modelValue) && modelValue?.length > 0 && !singleDisplay">
                <transition-group name="scale-fade">
                  <template v-if="Array.isArray(modelValue)">
                    <w-chip v-for="(option, index) in modelValue" :key="index" close @close="() => handleSelectItem(option)">
                      {{ options.find((e: any) => e.value === option)?.label }}
                    </w-chip>
                  </template>
                  <template v-else>
                    <w-chip :key="modelValue" close @close="() => handleSelectItem(modelValue)">
                      {{ options.find((e: any) => e.value === modelValue)?.label }}
                    </w-chip>
                  </template>
                </transition-group>
              </div>

              <!-- Enhanced chip display with maxChipsDisplay support -->
              <div
                class="new-chips flex items-center flex-wrap gap-1"
                v-if="Array.isArray(modelValue) && modelValue.length > 0 && singleDisplay"
              >
                <!-- Show up to maxChipsDisplay chips -->
                <template v-for="(value, index) in modelValue.slice(0, maxChipsDisplay)" :key="value">
                  <span class="w-chipX light line-clamp-1">
                    <slot name="chip" :option="options.find((e: any) => e.value === value)" :value="value">
                      {{ options.find((e: any) => e.value === value)?.label }}
                    </slot>
                  </span>
                </template>

                <!-- Show count if there are more items -->
                <span v-if="modelValue.length > maxChipsDisplay" class="count">+{{ modelValue.length - maxChipsDisplay }}</span>
              </div>

              <!-- single select input -->
              <ComboboxInput
                class="ComboboxInput"
                @focusin="focussed = true"
                @blur="handleBlur"
                v-show="multiple ? open || modelValue?.length == 0 : true"
              />
            </div>
          </template>

          <!-- dropdown icon and clear button -->
          <div class="ComboboxIcon__container">
            <w-tooltip content="Clear selection" v-if="(multiple && modelValue.length) || (modelValue && clearable)">
              <iconify-icon icon="fa6-solid:xmark" class="ComboboxIcon clear" @click.stop="clear" />
            </w-tooltip>
            <iconify-icon v-if="!$slots.trigger && showDropdownIcon" icon="fa6-solid:caret-down" class="ComboboxIcon" />
          </div>
        </ComboboxTrigger>
      </ComboboxAnchor>

      <!-- selectable options -->
      <ComboboxContent class="WSelectContent ComboboxContent" :position="position">
        <ComboboxViewport class="ComboboxViewport">
          <!-- Search Input (NEW) - only shows if showSearch prop is true -->
          <div v-if="showSearch" class="search-container">
            <div class="search-input-wrapper">
              <iconify-icon icon="fa6-solid:magnifying-glass" class="search-icon" />
              <input
                v-model="internalSearchTerm"
                type="text"
                :placeholder="searchPlaceholder"
                class="search-input"
                @input="handleSearchInput"
              />
            </div>
            <!-- Select All option (NEW) - only shows for multiple select with search -->
            <div v-if="multiple && showSelectAll" class="select-all-container">
              <button class="select-all-btn" @click="toggleSelectAll" :class="{ active: isAllSelected }">
                {{ selectAllText }}
              </button>
            </div>
          </div>

          <ComboboxEmpty v-if="filteredOptionsForDisplay.length === 0" class="ComboboxEmpty">
            {{ searchTerm ? 'No results found' : 'No options available' }}
          </ComboboxEmpty>

          <template v-for="(option, index) in filteredOptionsForDisplay" :key="index">
            <ComboboxItem
              v-if="filteredOptionsForDisplay.length <= 1000"
              class="ComboboxItem"
              :value="option"
              @select.prevent="() => handleSelectItem(option.value)"
            >
              <w-checkbox v-if="multiple && showCheckbox" :checked="modelValue?.includes(option.value)" />
              <slot name="option" :option="option">
                <span class="select-label">{{ option?.label }}</span>
              </slot>
            </ComboboxItem>
            <!-- light weight -->
            <div class="ComboboxItem" v-else @click="() => handleSelectItem(option.value)">
              <w-checkbox v-if="multiple && showCheckbox" :checked="modelValue?.includes(option.value)" />
              <slot name="option" :option="option">
                <span class="select-label">{{ option?.label }}</span>
              </slot>
            </div>
          </template>
        </ComboboxViewport>
      </ComboboxContent>
    </ComboboxRoot>
    <!-- Error-->
    <div v-if="error" class="smaller communication-danger select__error">
      <span>{{ error }}</span>
    </div>
  </div>
</template>

<script lang="ts">
  // @ts-nocheck

  import { defineComponent } from 'vue';
  import type { PropType } from 'vue';
  import * as R from 'radix-vue';

  interface IOption {
    label: string;
    value: string | number;
  }

  type IOptions = IOption[];

  export default defineComponent({
    name: 'WSelect',
    components: {
      ComboboxLabel: R.ComboboxLabel,
      ComboboxAnchor: R.ComboboxAnchor,
      ComboboxItem: R.ComboboxItem,
      ComboboxInput: R.ComboboxInput,
      ComboboxTrigger: R.ComboboxTrigger,
      ComboboxContent: R.ComboboxContent,
      ComboboxViewport: R.ComboboxViewport,
      ComboboxEmpty: R.ComboboxEmpty,
      ComboboxRoot: R.ComboboxRoot,
    },
    emits: ['change', 'blur', 'update:modelValue', 'search-change'],

    data() {
      return {
        searchTerm: '',
        open: false,
        focussed: false,
        internalSearchTerm: '', // NEW: separate search term for the search input
      };
    },

    props: {
      modelValue: [Array, String, Number, Object],
      options: Array as PropType<IOptions>,
      placeholder: String,
      multiple: Boolean,
      showCheckbox: {
        type: Boolean,
        default: true,
      },
      showDropdownIcon: {
        type: Boolean,
        default: true,
      },
      showSearchIcon: {
        type: Boolean,
        default: false,
      },
      disabled: Boolean,
      singleDisplay: Boolean,
      maxChipsDisplay: {
        type: Number,
        default: 1,
      },
      position: {
        type: String as 'inline' | 'popper',
        default: 'popper',
      },
      compact: Boolean,
      error: String,
      searchKeys: { type: Array as PropType[string[]], default: () => ['value', 'label'] },
      filterFunction: Function as PropType<(term: string) => any[] | undefined>,
      clearable: Boolean,
      hidePlaceholderOnFocus: Boolean,
      // NEW PROPS for search functionality
      showSearch: {
        type: Boolean,
        default: false, // Default false for backwards compatibility
      },
      searchPlaceholder: {
        type: String,
        default: 'Search name', // Matches your Figma design
      },
      showSelectAll: {
        type: Boolean,
        default: false, // Default false for backwards compatibility
      },
      selectAllText: {
        type: String,
        default: 'Select all',
      },
    },

    computed: {
      // NEW: Filter options based on internal search term when search is enabled
      filteredOptionsForDisplay(): IOptions {
        if (!this.showSearch || !this.internalSearchTerm) {
          return this.options || [];
        }
        return this.filterOptions(this.options || [], this.internalSearchTerm);
      },

      // NEW: Check if all filtered options are selected
      isAllSelected(): boolean {
        if (!this.multiple || !Array.isArray(this.modelValue)) return false;
        const filteredValues = this.filteredOptionsForDisplay.map(opt => opt.value);
        return filteredValues.length > 0 && filteredValues.every(val => this.modelValue.includes(val));
      },

      hasValidSingleValue() {
        return this.modelValue !== null && this.modelValue !== undefined && this.modelValue !== '' && this.modelValue !== false;
      },

      hasValidMultipleValue() {
        return Array.isArray(this.modelValue) && this.modelValue.length > 0;
      },
    },

    methods: {
      filterOptions(list: IOptions, term: string): IOptions {
        if (!list) return [];
        term = term.toLowerCase();
        return list?.filter((option: any) => {
          for (const key of this.searchKeys) {
            if (option[key].toString().toLowerCase().includes(term)) return true;
          }
          return false;
        });
      },

      handleOpenState(newState: boolean) {
        this.open = newState;
        if (!newState) {
          this.handleBlur();
          // Clear search when closing if showSearch is enabled
          if (this.showSearch) {
            this.internalSearchTerm = '';
          }
        }
      },

      handleBlur() {
        this.$emit('blur');
        this.focussed = false;
      },

      handleSelectItem(optionValue: any) {
        let newValue: any[] | any;
        if (this.multiple) {
          if (this.modelValue?.includes(optionValue)) {
            newValue = this.modelValue?.filter(e => e !== optionValue);
            this.$emit('update:modelValue', newValue);
          } else {
            newValue = [...this.modelValue, optionValue];
            this.$emit('update:modelValue', newValue);
          }
          this.searchTerm = '';
          this.$emit('change', newValue);
        } else {
          // update single value and close
          if (newValue !== optionValue) {
            newValue = optionValue;
            this.$emit('update:modelValue', newValue);
            this.$emit('change', newValue);
          }
          this.handleOpenState(false);
        }
      },

      clear() {
        if (this.multiple) this.$emit('update:modelValue', []);
        else this.$emit('update:modelValue', '');
      },

      // NEW: Handle search input
      handleSearchInput() {
        this.$emit('search-change', this.internalSearchTerm);
      },

      // NEW: Toggle select all functionality
      toggleSelectAll() {
        if (!this.multiple) return;

        const filteredValues = this.filteredOptionsForDisplay.map(opt => opt.value);
        const currentValues = Array.isArray(this.modelValue) ? this.modelValue : [];

        if (this.isAllSelected) {
          // Deselect all filtered options
          const newValue = currentValues.filter(val => !filteredValues.includes(val));
          this.$emit('update:modelValue', newValue);
          this.$emit('change', newValue);
        } else {
          // Select all filtered options
          const newValue = [...new Set([...currentValues, ...filteredValues])];
          this.$emit('update:modelValue', newValue);
          this.$emit('change', newValue);
        }
      },
    },

    watch: {
      searchTerm(newValue: string) {
        this.$emit('search-change', newValue);
      },
    },
  });
</script>

<style lang="scss">
  @import '@/assets/styles/variables';

  .WSelectContent.ComboboxContent {
    z-index: 10;
    max-height: 300px;
    width: var(--radix-popper-anchor-width);
    overflow: hidden;

    background-color: $white;
    border-radius: 0.5rem;
    border: 1px solid $input-border;
    margin-block: 0.25rem;
    box-shadow: 0 2px 5px $neutral-gray-10;
  }
</style>

<style lang="scss" scoped>
  @import '@/assets/styles/variables';

  .WSelect__wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    min-width: none;
    max-width: none;
    gap: 1rem;

    .select__error {
      padding: 0 1rem;
      line-height: 120%;
    }
  }

  // NEW: Search container styles
  .search-container {
    padding-inline: 24px;
    padding-top: 16px;
    padding-bottom: 10px;
    background-color: $white;

    .search-input-wrapper {
      position: relative;
      display: flex;
      align-items: center;

      .search-icon {
        position: absolute;
        left: 1rem;
        color: $neutral-gray;
        font-size: 0.875rem;
        z-index: 1;
      }

      .search-input {
        width: 100%;
        padding: 10px 0.75rem 10px 2rem;
        border: 1px solid $input-border;
        border-radius: 0.5rem;
        font-size: 0.875rem;
        background-color: $white;
        color: $neutral-black;

        &::placeholder {
          color: $input-placeholder;
        }

        &:focus {
          outline: none;
          border-color: $var-primary-light;
        }
      }
    }

    .select-all-container {
      margin-top: 16px;

      .select-all-btn {
        background: none;
        border: none;
        color: $neutral-gray;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        padding: 0;
        text-align: left;

        &:hover {
          color: $primary-main;
        }

        &.active {
          color: $primary-main;
          font-weight: 600;
        }
      }
    }
  }

  // EXISTING STYLES (unchanged for backwards compatibility)

  .ComboboxRoot {
    width: 50%;
    position: relative;

    * {
      transition: all 0.2;
    }
  }

  .ComboboxAnchor {
    display: flex;
    align-items: center;
    width: 100%;
    position: relative;
    z-index: 0;

    outline: none;

    &.optionDisabled {
      .ComboboxTrigger {
        background-color: $bg-dark;
      }

      * {
        cursor: not-allowed;
      }
    }
  }

  .ComboboxTrigger {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0.5rem 0;
    min-height: 3.5rem;
    height: fit-content;
    width: 100%;
    max-width: 100%;
    font-size: 1rem;
    overflow: hidden;

    border: 1px solid $input-border;
    background-color: $white;
    border-radius: 0.5rem;

    // NEW: Custom trigger styling
    &.hasCustomTrigger {
      .custom-trigger-content {
        // padding: 0 0.75rem;
        display: flex;
        align-items: center;
        min-height: inherit;
        min-width: 100%;
      }
    }

    &.singleDisplay {
      min-height: unset;
      height: 32px;
      .ComboboxInput {
        padding-top: 0 !important;
        padding-left: 0.75rem !important;
      }
    }
    &.hasFocus {
      border: 1px solid $var-primary-light;
    }

    .chips {
      display: none;
    }

    &.multiple {
      height: 100%;
    }
    .chips {
      width: 100%;
      z-index: 10;
      padding: 0 1.25rem;
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      cursor: text;
    }
    .new-chips {
      gap: 4px;
      padding: 0 0.75rem;
      .count {
        background-color: $primary-lighter;
        padding: 2px 6px;
        color: $primary-main;
        font-weight: 700;
        font-size: 10px;
        border-radius: 30px;
      }
    }
    .w-chipX {
      padding: 0.25rem 0.5rem;
      border-radius: 1rem;
      font-size: 0.75rem;
      gap: 5px;
      max-width: fit-content;
      min-width: fit-content;
      height: fit-content;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: $var-primary-lighter;
      color: $var-primary-main;
      border: 1px solid $var-primary-main;

      &.light,
      &.light.dot {
        background-color: $primary-lighter3;
        color: $neutral-black;
        border: 1px solid $primary-lighter;
      }
    }

    .ComboboxIcon__container {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.5rem;
      padding-right: 0.75rem;
      height: 100%;

      .ComboboxIcon {
        color: $neutral-gray;
        font-size: 1rem;
        cursor: pointer;

        &.clear {
          margin-top: 0.25rem;
        }
      }
    }

    // floating placeholder text
    .placeholder {
      z-index: 0;
      position: absolute;
      inset: 0;
      right: 3.5rem;
      color: $input-placeholder;
      font-size: 0.875rem;
      transition: all 0.2s;
      padding: 1.25rem 1.5rem;
      text-align: left;
      cursor: text;
      // text wrapping
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      &.singleDisplayPlaceholder {
        padding: 0.5rem 1.5rem;
      }
      &.hidden {
        opacity: 0;
        visibility: hidden;
      }
    }

    &.hasFocus.hasPlaceholder:not(.hidePlaceholderOnFocus),
    &.hasValue.hasPlaceholder:not(.hidePlaceholderOnFocus) {
      .placeholder {
        font-size: 0.75rem;
        padding: 0.5rem 1.5rem;
      }

      .chips {
        margin-top: 1rem;
      }
    }

    .ComboboxInput {
      padding-top: 0.75rem;
    }
  }

  .ComboboxInput {
    background-color: transparent;
    border: none;
    flex: 1;
    z-index: 10;
    height: 100%;
    width: 100%;
    padding-left: 1.5rem;
    font-size: 0.875rem;
    color: $neutral-black;

    &:focus-visible {
      outline: none;
    }
  }

  [data-radix-combobox-viewport] {
    scrollbar-width: 8px;
  }

  .ComboboxEmpty {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    text-align: center;
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: 500;
    color: $input-placeholder;
  }

  .ComboboxItem {
    max-width: 100%;
    gap: 1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 0.5rem;
    word-break: break-all;
    font-weight: 500;
    font-size: 0.875rem;
  }

  .ComboboxItem[data-disabled] {
    color: $input-placeholder;
    pointer-events: none;
  }

  .ComboboxItem[data-highlighted],
  .ComboboxItem:hover {
    outline: none;
    background-color: $bg-dark;
  }
  .select-label {
    word-break: keep-all;
    white-space: normal;
  }
</style>
